"use client";

import { Plus, X, PanelLeftClose } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { createGroup, deleteGroup } from "@/lib/group";
import logger from "@/lib/logger";
import useClientTranslation from "@/hooks/useClientTranslation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

export interface Group {
  id: string;
  name: string;
}

interface Props {
  onGroupChange?: (id: string | null) => void;
  onRefresh?: () => void;
  onHide?: () => void;
}

const STORAGE_KEY = "token-groups";

export default function GroupSidebar({ onGroupChange, onRefresh, onHide }: Props) {
  const { t } = useClientTranslation("logpg");
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem("sidebar-group") || null;
    }
    return null;
  });
  const [showAddInput, setShowAddInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Group[];
        const hasBadGroups = parsed.some(
          g =>
            typeof g?.id === "string" &&
            (g.id.includes("default-local") || g.id.startsWith("local-"))
        );
        if (hasBadGroups) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {
      logger.error("清理 localStorage 失败:", e);
    }
    loadGroupsFromStorage();
  }, []);

  const loadGroupsFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedGroups = JSON.parse(stored) as Group[];
        const validGroups = parsedGroups.filter(
          g => g.id && !g.id.includes("default-local") && !g.id.startsWith("local-")
        );
        if (validGroups.length > 0) {
          setGroups(validGroups);
          const savedId = sessionStorage.getItem("sidebar-group");
          const initId = savedId === "__all__"
            ? "__all__"
            : validGroups.find(g => g.id === savedId)?.id ?? validGroups[0]?.id ?? null;
          setSelectedGroupId(initId);
          onGroupChange?.(initId);
          fetchGroupsFromBackend();
          return;
        }
      }
      fetchGroupsFromBackend();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("加载本地分组失败: %s", msg);
      fetchGroupsFromBackend();
    }
  };

  const fetchGroupsFromBackend = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/applications");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const apps: { id: number; name: string }[] = await res.json();

      if (apps.length === 0) {
        setGroups([]);
        setSelectedGroupId(null);
        setError("noGroups");
        return;
      }

      // API 已按 position asc, created_time desc 排序
      const mappedGroups: Group[] = apps.map(a => ({
        id: String(a.id),
        name: a.name,
      }));

      setGroups(mappedGroups);
      const savedId = sessionStorage.getItem("sidebar-group");
      const initId = savedId === "__all__"
        ? "__all__"
        : mappedGroups.find(g => g.id === savedId)?.id ?? mappedGroups[0]?.id ?? null;
      setSelectedGroupId(initId);
      onGroupChange?.(initId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mappedGroups));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("加载分组失败: %s", msg);
      setError(msg || t("sidebar.loadFailed"));
      const defaultGroups = [{ id: "default-local", name: "" }];
      setGroups(defaultGroups);
      setSelectedGroupId("default-local");
      onGroupChange?.("default-local");
      localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultGroups));
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error(t("sidebar.inputGroupName"));
      return;
    }
    try {
      setLoading(true);
      await createGroup({ name: newGroupName.trim(), order: 0 });
      await fetchGroupsFromBackend();
      setNewGroupName("");
      setShowAddInput(false);
      toast.success(t("sidebar.addSuccess"));
      onRefresh?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("添加分组失败: %s", msg);
      toast.error(msg || t("sidebar.addFailed"));
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteGroup = async (groupId: string) => {
    if (groups.length <= 1) {
      toast.error(t("sidebar.keepOne"));
      return;
    }
    try {
      setLoading(true);
      await deleteGroup(groupId);
      const newGroups = groups.filter(g => g.id !== groupId);
      setGroups(newGroups);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newGroups));
      if (selectedGroupId === groupId) {
        const firstId = newGroups[0]?.id ?? null;
        setSelectedGroupId(firstId);
        onGroupChange?.(firstId);
      }
      onRefresh?.();
      toast.success(t("sidebar.deleteSuccess"));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("删除分组失败: %s", msg);
      toast.error(msg || t("sidebar.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const groupName = groups.find(g => g.id === groupId)?.name || t("sidebar.unknownGroup");
    setGroupToDelete({ id: groupId, name: groupName });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;
    const { id, name } = groupToDelete;
    setShowDeleteConfirm(false);
    setGroupToDelete(null);
    try {
      await confirmDeleteGroup(id);
      setIsDeleteMode(false);
    } catch {
      setShowDeleteConfirm(true);
      setGroupToDelete({ id, name });
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setGroupToDelete(null);
  };

  return (
    <div className="h-full w-56 shrink-0 flex flex-col border-r border-border bg-card">
      <div className="px-5 py-4 flex-shrink-0 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t("sidebar.title")}</h2>
        {onHide && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onHide}
          >
            <PanelLeftClose size={16} />
          </Button>
        )}
      </div>
      <Separator />

      <div className="flex-1 overflow-y-auto py-2">
        <div className="space-y-0.5 px-2">
          {/* 全部令牌 */}
          <button
            className={cn(
              "w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors",
              selectedGroupId === "__all__"
                ? "bg-primary/10 text-primary font-medium"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
            onClick={() => {
              setSelectedGroupId("__all__");
              onGroupChange?.("__all__");
            }}
          >
            <span className="truncate">{t("sidebar.allTokens")}</span>
          </button>
          {loading && groups.length === 0 ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">{t("sidebar.loading")}</p>
          ) : (
            groups.map(group => (
              <button
                key={group.id}
                className={cn(
                  "w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors",
                  selectedGroupId === group.id
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
                onClick={() => {
                  setSelectedGroupId(group.id);
                  onGroupChange?.(group.id);
                }}
              >
                <span className="truncate">{group.name || group.id}</span>
                {isDeleteMode && (
                  <X
                    size={14}
                    className="ml-1 shrink-0 text-destructive cursor-pointer"
                    onClick={(e) => handleDeleteGroup(group.id, e)}
                  />
                )}
              </button>
            ))
          )}
        </div>
      </div>

      <Separator />
      <div className="px-3 py-3 flex-shrink-0 space-y-2">
        {showAddInput ? (
          <div className="flex items-center gap-1">
            <Input
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAddGroup()}
              placeholder={t("sidebar.inputGroupNamePlaceholder")}
              className="h-8 text-sm"
              autoFocus
            />
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleAddGroup}>
              <Plus size={14} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 shrink-0"
              onClick={() => { setShowAddInput(false); setNewGroupName(""); }}
            >
              <X size={14} />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-8 text-xs"
              onClick={() => setShowAddInput(true)}
            >
              <Plus size={14} className="mr-1" />
              {t("sidebar.addGroup")}
            </Button>
            <Button
              variant={isDeleteMode ? "destructive" : "outline"}
              size="sm"
              className="h-8 text-xs"
              onClick={() => setIsDeleteMode(!isDeleteMode)}
            >
              {isDeleteMode ? t("sidebar.exitDelete") : t("sidebar.deleteMode")}
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("sidebar.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("sidebar.confirmDeleteMessage", { name: groupToDelete?.name || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>
              {t("sidebar.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
            >
              {t("sidebar.confirmDeleteBtn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}